import React, { Suspense, lazy } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './recorder-design-system.css';

import { RecorderAuthProvider } from './context/RecorderAuthContext';
import ProtectedRoute            from './routes/ProtectedRoute';
import RecorderProtectedRoute   from './routes/RecorderProtectedRoute';
import RecorderThemeProvider    from './components/recorder/RecorderThemeProvider';
import { buildBackendUrl } from './utils/api';
import { WorkspaceEntry, WorkspacePreferencesProvider } from './features/workspace/WorkspacePreferences';

const MainLayout = lazy(() => import('./layouts/MainLayout'));
const FocusLayout = lazy(() => import('./features/focus/FocusLayout'));
const FocusHome = lazy(() => import('./features/focus/FocusHome'));
const FocusTasks = lazy(() => import('./features/focus/FocusTasks'));
const FocusMeetings = lazy(() => import('./features/focus/FocusMeetings'));
const FocusMeeting = lazy(() => import('./features/focus/FocusMeeting'));
const FocusProjects = lazy(() => import('./features/focus/FocusProjects'));
const FocusProject = lazy(() => import('./features/focus/FocusProjects').then((module) => ({ default: module.FocusProject })));
const FocusInbox = lazy(() => import('./features/focus/FocusInbox'));
const FocusMailbox = lazy(() => import('./features/focus/FocusMailbox'));
const FocusEmailPage = lazy(() => import('./features/focus/FocusInbox').then(module => ({ default: module.FocusEmailPage })));
const FocusCalendar = lazy(() => import('./features/focus/FocusCalendar'));
const FocusProfile = lazy(() => import('./features/focus/FocusAccount').then(module => ({ default: module.FocusProfile })));
const FocusSettings = lazy(() => import('./features/focus/FocusAccount').then(module => ({ default: module.FocusSettings })));
const FocusExistingPage = lazy(() => import('./features/focus/FocusExistingPage'));
const FocusTeams = lazy(() => import('./features/focus/FocusPeople').then(module => ({ default: module.FocusTeams })));
const FocusOrganization = lazy(() => import('./features/focus/FocusPeople').then(module => ({ default: module.FocusOrganization })));
const FocusIntegrations = lazy(() => import('./features/focus/FocusIntegrations'));
const FocusReports = lazy(() => import('./features/focus/FocusReports'));
const FocusIntelligence = lazy(() => import('./features/focus/FocusIntelligence'));
const FocusProjectSetup = lazy(() => import('./features/focus/FocusProjectSetup'));
const Signup = lazy(() => import('./pages/Signup'));
const FocusBilling = lazy(() => import('./features/focus/FocusBilling'));
const LoginOnly = lazy(() => import('./pages/LoginOnly'));
const DashboardPage = lazy(() => import('./pages/Dashboard'));
const HomeV3Page = lazy(() => import('./pages/HomeV3Page'));
const InboxPage = lazy(() => import('./pages/InboxPage'));
const EmailPage = lazy(() => import('./pages/EmailPage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const ProjectPage = lazy(() => import('./pages/ProjectPage'));
const ProjectCreatePage = lazy(() => import('./pages/ProjectCreatePage'));
const ProjectEditPage = lazy(() => import('./pages/ProjectEditPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'));
const CommandCenterPage = lazy(() => import('./pages/CommandCenterPage'));
const WorkIntelligencePage = lazy(() => import('./pages/WorkIntelligencePage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const OrgInvitesSection = lazy(() => import('./pages/OrgInvitesSection'));
const MeetingsPage = lazy(() => import('./pages/MeetingsPage'));
const MeetingPage = lazy(() => import('./pages/MeetingPage'));
const MeetingBotTest = lazy(() => import('./pages/MeetingBotTest'));
const EmailBackfillPage = lazy(() => import('./pages/EmailBackfillPage'));
const RecorderSignupPage = lazy(() => import('./pages/RecorderSignupPage'));
const RecorderVerifyPage = lazy(() => import('./pages/RecorderVerifyPage'));
const RecorderLoginPage = lazy(() => import('./pages/RecorderLoginPage'));
const RecorderForgotPasswordPage = lazy(() => import('./pages/RecorderForgotPasswordPage'));
const RecorderResetPasswordPage = lazy(() => import('./pages/RecorderResetPasswordPage'));
const RecorderHomePage = lazy(() => import('./pages/RecorderHomePage'));
const BepSummitRecorderPage = lazy(() => import('./pages/BepSummitRecorderPage'));
const RecorderLibraryPage = lazy(() => import('./pages/RecorderLibraryPage'));
const RecorderDetailPage = lazy(() => import('./pages/RecorderDetailPage'));
const RecorderSettingsPage = lazy(() => import('./pages/RecorderSettingsPage'));

function RouteErrorFallback() {
  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'var(--rc-bg)', color: 'var(--rc-text)' }}
    >
      <div
        className="max-w-md rounded-2xl border px-6 py-5 text-center shadow-sm"
        style={{
          background: 'var(--rc-surface)',
          borderColor: 'var(--rc-border-subtle)',
        }}
      >
        <div className="text-base font-semibold" style={{ color: 'var(--rc-text)' }}>
          InboxOps needs a refresh
        </div>
        <p className="mt-2 text-sm leading-6" style={{ color: 'var(--rc-text-2)' }}>
          A screen could not load cleanly. This can happen after a deployment if your browser has an older app chunk.
        </p>
        <button
          type="button"
          className="mt-4 inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-semibold"
          style={{ background: 'var(--rc-accent)', color: '#fff' }}
          onClick={() => window.location.reload()}
        >
          Refresh app
        </button>
      </div>
    </div>
  );
}

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Route render failed:', error, info);
    }
  }

  render() {
    if (this.state.hasError) return <RouteErrorFallback />;
    return this.props.children;
  }
}

function PageLoading() {
  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'var(--rc-bg)', color: 'var(--rc-text)' }}
    >
      <div
        className="rounded-2xl border px-5 py-4 text-sm font-semibold shadow-sm"
        style={{
          background: 'var(--rc-surface)',
          borderColor: 'var(--rc-border-subtle)',
          color: 'var(--rc-text-2)',
        }}
      >
        Loading InboxOps...
      </div>
    </div>
  );
}

function BackendRedirect() {
  React.useEffect(() => {
    const path = `${window.location.pathname}${window.location.search || ''}`;
    window.location.replace(buildBackendUrl(path));
  }, []);

  return <PageLoading />;
}

function AppInner() {
  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        newestOnTop
        hideProgressBar
      />

      <RouteErrorBoundary>
        <Suspense fallback={<PageLoading />}>
          <Routes>
            <Route path="/" element={<Navigate to="/workspace" replace />} />
            <Route path="/team" element={<Navigate to="/dashboard/teams" replace />} />
            <Route path="/teams" element={<Navigate to="/dashboard/teams" replace />} />
            <Route path="/tasks" element={<Navigate to="/dashboard/tasks" replace />} />
            <Route path="/beta-lab" element={<Navigate to="/dashboard/command-center" replace />} />
            <Route path="/command-center" element={<Navigate to="/dashboard/command-center" replace />} />
            <Route path="/intelligence" element={<Navigate to="/dashboard/intelligence" replace />} />
            <Route path="/ai" element={<Navigate to="/dashboard/command-center" replace />} />
            <Route path="/assistant" element={<Navigate to="/dashboard/command-center" replace />} />

            <Route path="/signup" element={<Signup />} />
            <Route path="/login" element={<LoginOnly />} />
            <Route path="/auth" element={<BackendRedirect />} />
            <Route path="/auth/google/login" element={<BackendRedirect />} />
            <Route path="/auth/microsoft" element={<BackendRedirect />} />
            <Route path="/auth/microsoft/login" element={<BackendRedirect />} />
            <Route path="/auth/asana" element={<BackendRedirect />} />
            <Route path="/api/atlassian/login" element={<BackendRedirect />} />

            <Route path="/recorder-signup" element={<RecorderSignupPage />} />
            <Route path="/recorder-verify" element={<RecorderVerifyPage />} />
            <Route path="/recorder-login" element={<RecorderLoginPage />} />
            <Route path="/recorder-forgot-password" element={<RecorderForgotPasswordPage />} />
            <Route path="/recorder-reset-password" element={<RecorderResetPasswordPage />} />

            <Route element={<ProtectedRoute />}>
              <Route element={<WorkspacePreferencesProvider />}>
              <Route path="/workspace" element={<WorkspaceEntry />} />
              <Route path="/focus" element={<FocusLayout />}>
                <Route index element={<FocusHome />} />
                <Route path="tasks" element={<FocusTasks />} />
                <Route path="meetings" element={<FocusMeetings />} />
                <Route path="meetings/:id" element={<FocusMeeting />} />
                <Route path="inbox" element={<FocusInbox />} />
                <Route path="inbox/mailboxes" element={<FocusMailbox />} />
                <Route path="inbox/:summaryId/:messageId" element={<FocusEmailPage />} />
                <Route path="calendar" element={<FocusCalendar />} />
                <Route path="projects" element={<FocusProjects />} />
                <Route path="projects/new" element={<FocusProjectSetup />} />
                <Route path="projects/:projectId" element={<FocusProject />} />
                <Route path="projects/:projectId/edit" element={<FocusProjectSetup />} />
                <Route path="intelligence" element={<FocusIntelligence />} />
                <Route path="reports" element={<FocusReports />} />
                <Route path="teams" element={<FocusTeams />} />
                <Route path="org-invites" element={<FocusOrganization />} />
                <Route path="integrations" element={<FocusIntegrations />} />
                <Route path="profile" element={<FocusProfile />} />
                <Route path="settings" element={<FocusSettings />} />
                <Route path="billing" element={<FocusBilling />} />
                <Route path="command-center" element={<FocusExistingPage screen="command" />} />
              </Route>
              <Route path="/dashboard" element={<MainLayout />}>
                <Route index element={<HomeV3Page />} />
                <Route path="home-v3" element={<Navigate to="/dashboard" replace />} />
                <Route path="legacy-dashboard" element={<DashboardPage />} />
                <Route path="inbox" element={<InboxPage />} />
                <Route path="inbox/:summaryId/:messageId" element={<EmailPage />} />

                <Route path="calendar" element={<CalendarPage />} />

                <Route path="tasks" element={<TasksPage />} />

                <Route path="meeting-bot-test" element={<MeetingBotTest />} />

                <Route path="projects">
                  <Route index element={<ProjectsPage />} />
                  <Route path="new" element={<ProjectCreatePage />} />
                  <Route path=":projectId" element={<ProjectPage />} />
                  <Route path=":projectId/edit" element={<ProjectEditPage />} />
                </Route>

                <Route path="meetings" element={<MeetingsPage />} />
                <Route path="meetings/:id" element={<MeetingPage />} />

                <Route path="team" element={<Navigate to="/dashboard/teams" replace />} />
                <Route path="teams" element={<TeamPage />} />

                <Route path="org-invites" element={<OrgInvitesSection />} />

                <Route path="reports" element={<ReportsPage />} />
                <Route path="integrations" element={<IntegrationsPage />} />
                <Route path="command-center" element={<CommandCenterPage />} />
                <Route path="intelligence" element={<WorkIntelligencePage />} />
                <Route path="command" element={<Navigate to="/dashboard/command-center" replace />} />
                <Route path="ai" element={<Navigate to="/dashboard/command-center" replace />} />
                <Route path="assistant" element={<Navigate to="/dashboard/command-center" replace />} />
                <Route path="beta" element={<Navigate to="/dashboard/command-center" replace />} />
                <Route path="beta-lab" element={<Navigate to="/dashboard/command-center" replace />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="billing" element={<div className="focus-design io-classic-billing"><FocusBilling /></div>} />
              </Route>

              <Route path="/email-backfill" element={<MainLayout />}>
                <Route index element={<EmailBackfillPage />} />
              </Route>
              </Route>
            </Route>

            <Route
              path="/recorder/bep-summit-2026"
              element={
                <RecorderThemeProvider>
                  <BepSummitRecorderPage />
                </RecorderThemeProvider>
              }
            />

            <Route element={<RecorderProtectedRoute />}>
              <Route
                path="/recorder"
                element={
                  <RecorderThemeProvider>
                    <RecorderHomePage />
                  </RecorderThemeProvider>
                }
              />
              <Route
                path="/recorder/recordings"
                element={
                  <RecorderThemeProvider>
                    <RecorderLibraryPage />
                  </RecorderThemeProvider>
                }
              />
              <Route
                path="/recorder/recordings/:id"
                element={
                  <RecorderThemeProvider>
                    <RecorderDetailPage />
                  </RecorderThemeProvider>
                }
              />
              <Route
                path="/recorder/settings"
                element={
                  <RecorderThemeProvider>
                    <RecorderSettingsPage />
                  </RecorderThemeProvider>
                }
              />
            </Route>

            <Route path="*" element={<div className="p-8">404: Page not found</div>} />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
    </>
  );
}

function App() {
  return (
    <Router>
      <RecorderAuthProvider>
        <AppInner />
      </RecorderAuthProvider>
    </Router>
  );
}

export default App;
